// src/pages/ResultsPage.js
import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import API_BASE from '../config';

const ResultsPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session');
  const rounds = searchParams.get('rounds');
  const guessMode = searchParams.get('guessMode');
  const gameMode = searchParams.get('mode');
  const alpha = searchParams.get('alpha') || '0.1';
  const commonNameLang = searchParams.get('commonNameLang') || 'en';
  const genera = searchParams.get('genera') || '';
  const taxaParam = searchParams.get('taxa') || '';
  const placeId = searchParams.get('place_id') || '';
  const countryName = searchParams.get('country') || '';
  const nelat = searchParams.get('nelat');
  const nelng = searchParams.get('nelng');
  const swlat = searchParams.get('swlat');
  const swlng = searchParams.get('swlng');
  const hasBbox = nelat && nelng && swlat && swlng;

  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [playingAgain, setPlayingAgain] = useState(false);

  useEffect(() => {
    const fetchResults = async () => {
      try {
        const res = await fetch(`${API_BASE}/get_results?session_id=${sessionId}`);
        if (!res.ok) {
          const text = await res.text();
          throw new Error(text || `Error fetching results: ${res.status}`);
        }
        const data = await res.json();
        if (data.error) {
          throw new Error(data.error);
        }
        setResults(data);
      } catch (err) {
        setError(err.message || 'Failed to load results');
      } finally {
        setLoading(false);
      }
    };

    fetchResults();
  }, [sessionId]);

  const handlePlayAgain = async () => {
    setPlayingAgain(true);
    try {
      const startRes = await fetch(`${API_BASE}/start_game`);
      const startData = await startRes.json();
      if (!startRes.ok || !startData.session_id) throw new Error('Failed to start session');
      const newSession = startData.session_id;

      if (gameMode === 'country') {
        const body = {
          alpha: parseFloat(alpha),
          num_rounds: parseInt(rounds),
          session_id: newSession,
          guess_mode: guessMode,
          common_name_language: commonNameLang,
        };
        if (hasBbox) {
          Object.assign(body, { nelat: parseFloat(nelat), nelng: parseFloat(nelng), swlat: parseFloat(swlat), swlng: parseFloat(swlng) });
        } else {
          body.place_id = parseInt(placeId);
        }
        await fetch(`${API_BASE}/prefetch_obs_by_country`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
        const gameUrl = hasBbox
          ? `/game?rounds=${rounds}&session=${newSession}&guessMode=${guessMode}&mode=country&alpha=${alpha}&commonNameLang=${encodeURIComponent(commonNameLang)}&nelat=${nelat}&nelng=${nelng}&swlat=${swlat}&swlng=${swlng}${countryName ? `&country=${encodeURIComponent(countryName)}` : ''}`
          : `/game?rounds=${rounds}&session=${newSession}&guessMode=${guessMode}&mode=country&alpha=${alpha}&commonNameLang=${encodeURIComponent(commonNameLang)}&place_id=${placeId}&country=${encodeURIComponent(countryName)}`;
        navigate(gameUrl);
      } else if (gameMode === 'taxa') {
        let taxaList = [];
        try { taxaList = JSON.parse(taxaParam) || []; } catch (_) { taxaList = []; }
        await fetch(`${API_BASE}/prefetch_obs_by_taxa`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            taxa: taxaList,
            alpha: parseFloat(alpha),
            num_rounds: parseInt(rounds),
            session_id: newSession,
            guess_mode: guessMode,
            common_name_language: commonNameLang,
          }),
        });
        navigate(`/game?rounds=${rounds}&session=${newSession}&guessMode=${guessMode}&mode=taxa&alpha=${alpha}&commonNameLang=${encodeURIComponent(commonNameLang)}&taxa=${encodeURIComponent(taxaParam)}`);
      } else {
        const generaList = genera.split(',').filter(Boolean);
        await fetch(`${API_BASE}/prefetch_obs_balanced`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            user_input: generaList,
            alpha: parseFloat(alpha),
            num_rounds: parseInt(rounds),
            session_id: newSession,
            guess_mode: guessMode,
            common_name_language: commonNameLang,
          }),
        });
        navigate(`/game?rounds=${rounds}&session=${newSession}&guessMode=${guessMode}&mode=${gameMode}&alpha=${alpha}&commonNameLang=${encodeURIComponent(commonNameLang)}&genera=${encodeURIComponent(genera)}`);
      }
    } catch (err) {
      console.error('Play again failed:', err);
      setPlayingAgain(false);
    }
  };

  if (loading) {
    return (
      <div>
        <div className="page-header">
          <button className="page-title-btn" onClick={() => navigate('/')}><img src="/logo.png?v=2" alt="" className="header-logo" />PlantID Game</button>
        </div>
        <div style={{ textAlign: 'center', marginTop: '50px' }}>
          <h1>Results</h1>
          <p>Loading results...</p>
        </div>
      </div>
    );
  }

  if (error || !results) {
    return (
      <div>
        <div className="page-header">
          <button className="page-title-btn" onClick={() => navigate('/')}><img src="/logo.png?v=2" alt="" className="header-logo" />PlantID Game</button>
        </div>
        <div style={{ textAlign: 'center', marginTop: '50px' }}>
          <h1>Results</h1>
          <p style={{ color: 'red' }}>{error || 'No results available.'}</p>
          <button onClick={() => navigate('/')} style={{ padding: '10px 20px', fontSize: '16px' }}>
            Start page
          </button>
        </div>
      </div>
    );
  }

  const { total_rounds, num_correct, accuracy, guesses } = results;

  return (
    <div>
      <div className="page-header">
        <button className="page-title-btn" onClick={() => navigate('/')}><img src="/logo.png?v=2" alt="" className="header-logo" />PlantID Game</button>
      </div>
      <div style={{ textAlign: 'center', marginTop: '50px' }}>
      <h1>Results</h1>

      <p>
        You got {num_correct} out of {total_rounds} correct (
        {Math.round((accuracy || 0) * 100)}%).
      </p>

      {guesses && guesses.length > 0 && (
        <table
          style={{
            margin: '20px auto',
            borderCollapse: 'collapse',
            minWidth: '60%',
          }}
        >
          <thead>
            <tr>
              <th style={{ border: '1px solid #ccc', padding: '8px' }}>Round</th>
              <th style={{ border: '1px solid #ccc', padding: '8px' }}>Image</th>
              <th style={{ border: '1px solid #ccc', padding: '8px' }}>Your guess</th>
              <th style={{ border: '1px solid #ccc', padding: '8px' }}>Scientific name</th>
              <th style={{ border: '1px solid #ccc', padding: '8px' }}>Common name</th>
              <th style={{ border: '1px solid #ccc', padding: '8px' }}>Correct?</th>
            </tr>
          </thead>
          <tbody>
            {guesses.map((g, idx) => (
              <tr
                key={idx}
                style={{
                  backgroundColor: g.correct
                    ? 'rgba(100, 200, 90, 0.18)'
                    : 'rgba(220, 70, 70, 0.18)',
                }}
              >
                <td style={{ border: '1px solid #ccc', padding: '8px' }}>
                  {(g.round ?? idx) + 1}
                </td>
                <td style={{ border: '1px solid #ccc', padding: '8px' }}>
                  {g.image_path && (
                    <img
                      src={`${API_BASE}/${g.image_path}`}
                      alt={g.scientific_name}
                      style={{ width: '80px', height: '80px', objectFit: 'cover' }}
                    />
                  )}
                </td>
                <td style={{ border: '1px solid #ccc', padding: '8px' }}>{g.user_guess}</td>
                <td style={{ border: '1px solid #ccc', padding: '8px' }}>{g.scientific_name}</td>
                <td style={{ border: '1px solid #ccc', padding: '8px' }}>{g.common_name}</td>
                <td
                  style={{
                    border: '1px solid #ccc',
                    padding: '8px',
                    fontWeight: 'bold',
                    color: g.correct ? '#4caf50' : '#e53935',
                  }}
                >
                  {g.correct ? '✓' : '✗'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', marginTop: '24px', flexWrap: 'wrap' }}>
        <button onClick={() => navigate('/')} style={{ padding: '10px 20px', fontSize: '16px' }}>
          Start page
        </button>
        <button onClick={() => navigate('/settings')} style={{ padding: '10px 20px', fontSize: '16px' }}>
          Settings
        </button>
        {rounds && gameMode && (
          <button onClick={handlePlayAgain} disabled={playingAgain} style={{ padding: '10px 20px', fontSize: '16px' }}>
            {playingAgain ? 'Starting…' : 'Play again'}
          </button>
        )}
      </div>
      </div>
    </div>
  );
};

export default ResultsPage;
